/*
||--------------------------------------------------------------------------
|| Component: VariantAttributeCard.jsx
|| Author: Moaz
|| Editing: Sayed Ahmed
|| Description: Displays a card for a single product attribute,
||              allows toggling the attribute and selecting its values.
||              Enhanced with better UI, animations, and user experience.
|| Notes: Used in product variant generation workflow.
||--------------------------------------------------------------------------
*/

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../css/variant-improvements.css";

export default function VariantAttributeCard({
  attribute,
  selected,
  selectedValues = [],
  onToggleAttribute,
  onToggleValue,
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(selected);

  const isValueSelected = (valueId) =>
    selectedValues.some((v) => v.id == valueId);

  const handleAttributeToggle = (e) => {
    const isChecked = e.target.checked;
    onToggleAttribute(attribute.id, isChecked);
    setIsExpanded(isChecked);
  };

  const handleValueToggle = (valueId, newState) => {
    onToggleValue(attribute.id, valueId, newState);
  };

  const selectedCount = selectedValues.length;
  const totalCount = attribute.values?.length || 0;

  return (
    <div className="card shadow-sm border-0 mb-3 variant-attribute-card animate-fade-in">
      {/* Header: Toggle Attribute */}
      <div className="card-header bg-white border-bottom-0 d-flex align-items-center justify-content-between p-3 card-header-enhanced">
        <div className="form-check d-flex align-items-center">
          <input
            type="checkbox"
            className="form-check-input me-3"
            id={`attribute-${attribute.id}`}
            checked={selected}
            onChange={handleAttributeToggle}
            style={{ transform: "scale(1.2)" }}
          />
          <label
            htmlFor={`attribute-${attribute.id}`}
            className="form-check-label fw-bold mb-0 d-flex align-items-center"
            style={{ cursor: "pointer" }}
          >
            <i className="fas fa-tag me-2 text-primary"></i>
            {attribute.name}
            {selected && (
              <span className="badge bg-primary ms-2">
                {selectedCount}/{totalCount}
              </span>
            )}
          </label>
        </div>
        
        {selected && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? t("Collapse") : t("Expand")}
          >
            <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
          </button>
        )}
      </div>

      {/* Body: Value Selection */}
      {selected && isExpanded && (
        <div className="card-body p-3 pt-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <label className="form-label fw-semibold mb-0">
              <i className="fas fa-list me-2 text-muted"></i>
              {t("Select Values")}
            </label>
            {selectedCount > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  // Clear all selected values
                  selectedValues.forEach(value => {
                    onToggleValue(attribute.id, value.id, false);
                  });
                }}
                title={t("Clear All")}
              >
                <i className="fas fa-times me-1"></i>
                {t("Clear All")}
              </button>
            )}
          </div>

          <div className="d-flex flex-wrap gap-2">
            {(attribute.values || []).length > 0 ? (
              attribute.values.map((value, index) => {
                const isChecked = isValueSelected(value.id);
                return (
                  <label
                    key={index}
                    className={`p-3 rounded-3 border d-flex align-items-center position-relative value-selection-item ${
                      isChecked 
                        ? "selected" 
                        : "bg-light text-dark border-light"
                    }`}
                    style={{ 
                      cursor: "pointer", 
                      minWidth: "140px",
                      transition: "all 0.3s ease",
                      transform: isChecked ? "scale(1.02)" : "scale(1)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked) {
                        e.target.style.backgroundColor = "#f8f9fa";
                        e.target.style.borderColor = "#dee2e6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isChecked) {
                        e.target.style.backgroundColor = "#f8f9fa";
                        e.target.style.borderColor = "#f8f9fa";
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input me-3"
                      checked={isChecked}
                      onChange={(e) => handleValueToggle(value.id, e.target.checked)}
                      style={{ transform: "scale(1.1)" }}
                    />
                    
                    <div className="d-flex align-items-center flex-grow-1">
                      <span className="fw-medium">{value.value}</span>
                      
                      {value.hex && (
                        <div
                          className="ms-auto rounded-circle border"
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: value.hex,
                            border: isChecked ? "2px solid white" : "2px solid #dee2e6",
                            boxShadow: isChecked ? "0 0 0 1px rgba(0,0,0,0.1)" : "none",
                          }}
                          title={value.value}
                        />
                      )}
                    </div>

                    {/* Selection indicator */}
                    {isChecked && (
                      <div
                        className="position-absolute top-0 end-0 bg-white text-primary rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 24,
                          height: 24,
                          fontSize: 12,
                          transform: "translate(25%, -25%)",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        ✓
                      </div>
                    )}
                  </label>
                );
              })
            ) : (
              <div className="text-center text-muted py-4 w-100">
                <i className="fas fa-exclamation-triangle fa-2x mb-2"></i>
                <div>{t("No values available")}</div>
              </div>
            )}
          </div>

          {/* Selection Summary */}
          {selectedCount > 0 && (
            <div className="mt-3 p-2 bg-light rounded-2">
              <div className="d-flex align-items-center text-muted small">
                <i className="fas fa-info-circle me-2"></i>
                <span>
                  {selectedCount == totalCount 
                    ? t("All values selected") 
                    : t("{{count}} of {{total}} values selected", { count: selectedCount, total: totalCount })
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collapsed Summary */}
      {selected && !isExpanded && selectedCount > 0 && (
        <div className="card-body p-3 pt-0">
          <div className="d-flex flex-wrap gap-1">
            {selectedValues.slice(0, 3).map((value, index) => (
              <span key={index} className="badge bg-primary">
                {value.value}
                {value.hex && (
                  <span
                    className="ms-1 rounded-circle"
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      backgroundColor: value.hex,
                      border: "1px solid white",
                    }}
                  />
                )}
              </span>
            ))}
            {selectedCount > 3 && (
              <span className="badge bg-secondary">
                +{selectedCount - 3} {t("more")}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}